// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';
import { 
  signInUser, 
  signOutUser, 
  getLoggedInUserProfile,
  checkAuthStatus,
  getStoredUserData
} from '../services/authService'; 

// Zustand store for authentication state
const useAuthStore = create((set) => ({
  isAuthenticated: false,
  user: null,
  isLoading: true, // To indicate initial auth check is in progress
  setAuth: (isAuthenticated, user) => set({ isAuthenticated, user, isLoading: false }),
  clearAuth: () => set({ isAuthenticated: false, user: null, isLoading: false }),
  setLoading: (loading) => set({ isLoading: loading }),
}));

// React Context for Auth
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading, setAuth, clearAuth, setLoading } = useAuthStore();
  const navigate = useNavigate();

  // Effect to check authentication status on app load
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      
      try {
        // Check if user is authenticated (has valid tokens)
        if (checkAuthStatus()) {
          const storedUser = getStoredUserData();
          
          if (storedUser) {
            // Try to get fresh user data from backend
            try {
              const freshUserData = await getLoggedInUserProfile();
              // Extract the actual user profile from the API response
              const userProfile = freshUserData.profile || freshUserData;
              setAuth(true, userProfile);
            } catch (error) {
              // If API call fails, use stored data but log the error
              console.warn('Failed to fetch fresh user data, using stored data:', error);
              setAuth(true, storedUser);
            }
          } else {
            // No stored user data, clear auth
            clearAuth();
            navigate('/login', { replace: true });
          }
        } else {
          // Not authenticated
          clearAuth();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [setAuth, clearAuth, setLoading, navigate]);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const data = await signInUser(username, password);
      setAuth(true, data.user);
      navigate('/dashboard', { replace: true });
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      clearAuth();
      return { success: false, error: error.message || 'Login Failed' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOutUser();
      clearAuth();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout API fails, clear local state
      clearAuth();
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const refreshUserProfile = async () => {
    try {
      const freshUserData = await getLoggedInUserProfile();
      setAuth(true, freshUserData);
      return freshUserData;
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      isLoading, 
      login, 
      logout,
      refreshUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};